import { stripEmoji, stripLeadingSpecialChars, stripTrailingSpecialChars } from './formatting';

const NON_TEXT_INPUT_TYPES = new Set([
    'checkbox', 'radio', 'file', 'range', 'color', 'button', 'submit',
    'reset', 'hidden', 'image', 'date', 'time', 'datetime-local', 'month', 'week',
]);

const setNativeValue = (element, value) => {
    const proto = element.tagName === 'TEXTAREA' ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype;
    const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
    if (setter) {
        setter.call(element, value);
    } else {
        element.value = value;
    }
};

// Safety-net for text fields that don't go through CommonInputField/CommonTextField
// (raw <input>/<textarea>, Blueprint EditableText, etc). Runs in the capture phase so the
// DOM value is already cleaned before React's own bubble-phase listener reads it.
const handleInputCapture = (e) => {
    // Never touch a value mid-IME-composition (e.g. typing Chinese/Japanese/Korean) -
    // interrupting composition here would corrupt the text being composed.
    if (e.isComposing) return;

    const el = e.target;
    if (!el) return;
    const tag = el.tagName;
    if (tag !== 'INPUT' && tag !== 'TEXTAREA') return;
    if (tag === 'INPUT' && NON_TEXT_INPUT_TYPES.has((el.type || 'text').toLowerCase())) return;

    const rawVal = el.value;
    const cleaned = stripLeadingSpecialChars(stripEmoji(rawVal));
    if (cleaned === rawVal) return;

    const selectionStart = el.selectionStart;
    const diff = rawVal.length - cleaned.length;

    setNativeValue(el, cleaned);

    if (typeof selectionStart === 'number') {
        const pos = Math.max(0, selectionStart - diff);
        try {
            el.setSelectionRange(pos, pos);
        } catch (err) {
            // selectionRange isn't supported on some input types (e.g. email/number in some browsers)
        }
    }
};

// Trailing-special-char cleanup runs on blur (focusout), not on every keystroke - see
// stripTrailingSpecialChars for why. Fires in the capture phase, ahead of React's own
// root-level focusout listener, then re-dispatches a native 'input' event so React's
// controlled state (and any onChange-driven side effects) pick up the cleaned value too.
const handleFocusOutCapture = (e) => {
    const el = e.target;
    if (!el) return;
    const tag = el.tagName;
    if (tag !== 'INPUT' && tag !== 'TEXTAREA') return;
    if (tag === 'INPUT' && NON_TEXT_INPUT_TYPES.has((el.type || 'text').toLowerCase())) return;

    const rawVal = el.value;
    const cleaned = stripTrailingSpecialChars(rawVal);
    if (cleaned === rawVal) return;

    setNativeValue(el, cleaned);
    el.dispatchEvent(new Event('input', { bubbles: true }));
};

let initialized = false;

export const initGlobalEmojiGuard = () => {
    if (initialized) return;
    initialized = true;
    document.addEventListener('input', handleInputCapture, true);
    document.addEventListener('focusout', handleFocusOutCapture, true);
};
